// apps/workflow/src/pages/freelancer/OnboardingStatus.jsx
import React, { useState } from 'react';
import {
  Search,
  User,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const FreelancerOnboardingStatus = () => {
  const [email, setEmail] = useState('');
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    setNotFound(false);
    setApplication(null);
    try {
      const records = await pb.collection('freelancer_applications').getFullList({
        filter: `email = "${email}"`,
        sort: '-created',
        requestKey: null,
      });
      if (records.length === 0) {
        setNotFound(true);
        toast.info('No application found with this email');
      } else {
        // Expand user to show more details if needed
        const app = await pb.collection('freelancer_applications').getOne(records[0].id, { expand: 'user' });
        setApplication(app);
        toast.success('Application found');
      }
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error checking status:', error);
        toast.error('Failed to check status');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const config = {
      pending: { label: 'Pending Review', icon: Clock, color: 'text-amber-500' },
      approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-500' },
      rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-500' },
    };
    return config[status] || { label: status, icon: AlertCircle, color: 'text-slate-500' };
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
        <h1 className="text-2xl font-bold text-slate-800 text-center mb-2">
          Application Status
        </h1>
        <p className="text-sm text-slate-500 text-center mb-6">
          Check the status of your freelancer application using your email.
        </p>

        <form onSubmit={handleCheck} className="flex gap-3 mb-6">
          <div className="flex-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Check
          </button>
        </form>

        {notFound && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-amber-700">
              No application found with this email address.
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Please make sure you used the same email when applying.
            </p>
          </div>
        )}

        {application && (
          <div className="bg-slate-50 rounded-lg p-6 space-y-3 border border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Application Details</h3>
              <span className="text-xs text-slate-400">
                Submitted: {new Date(application.created).toLocaleDateString()}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Name:</span>
                <span className="font-medium text-slate-700 ml-2">{application.name}</span>
              </div>
              <div>
                <span className="text-slate-500">Email:</span>
                <span className="font-medium text-slate-700 ml-2">{application.email}</span>
              </div>
              <div>
                <span className="text-slate-500">Phone:</span>
                <span className="font-medium text-slate-700 ml-2">{application.phone || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">Status:</span>
                <span className={`ml-2 font-medium ${getStatusDisplay(application.status).color}`}>
                  {getStatusDisplay(application.status).label}
                </span>
              </div>
              {application.expand?.user && (
                <div className="col-span-2">
                  <span className="text-slate-500">User Role:</span>
                  <span className="font-medium text-slate-700 ml-2">{application.expand.user.role}</span>
                </div>
              )}
            </div>

            {application.experience && (
              <div>
                <span className="text-slate-500 text-sm">Experience:</span>
                <p className="text-sm text-slate-700 mt-1">{application.experience}</p>
              </div>
            )}

            {application.status === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2">
                <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
                <span className="text-sm text-green-700">
                  Congratulations! Your application has been approved. You can now log in as a freelancer.
                  Go to your dashboard to activate your subscription.
                </span>
              </div>
            )}

            {application.status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-2">
                <XCircle className="w-5 h-5 text-red-600 inline mr-2" />
                <span className="text-sm text-red-700">
                  We regret to inform you that your application has been rejected. Please contact support for more information.
                </span>
              </div>
            )}

            {application.status === 'pending' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                <Clock className="w-5 h-5 text-amber-600 inline mr-2" />
                <span className="text-sm text-amber-700">
                  Your application is currently under review. We'll notify you once a decision is made.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FreelancerOnboardingStatus;